import { LessonLearntFilter } from './../../model/lessons-learnt/LessonLearntFilter';
import { ProjectPhase, phaseToLabel } from './../../model/general/ProjectPhase';
import { DropDownService } from 'src/app/shared/services/other/drop-down.service';
import { DropDownOption } from './../../model/drop-down/DropDownOption';
import { GetUsers } from './../../services/user/GetUsers';
import { UserService } from './../../services/user/user.service';
import { ProjectListService } from './../../services/project-list/project-list.service';
import { LessonLearnt } from './../../model/lessons-learnt/LessonLearnt';
import { Subject,  debounceTime } from 'rxjs';
import { Component, OnInit, Input, ViewChild, AfterViewInit } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DateUtil } from '../../DateUtil';
import { Technology, technologyToLabel } from '../../model/general/Technology';
import { ALL_COUNTRIES, BY_ALPHA_2 } from '../../model/geo/Countries';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { LessonsLearntService } from '../../services/lessons-learnt/lessons-learnt.service';
import { MatDialog } from '@angular/material/dialog';
import { LessonsLearntDialogComponent } from './lessons-learnt-dialog/lessons-learnt-dialog.component';
import { CountryAlpha2Code } from '../../model/geo/CountryAlpha2Code';
import { DiscussedInTeam, discussedInTeamToLabel } from '../../model/lessons-learnt/DiscussedInTeam';
import { LessonLearntBestPractice, lessonLearntBestPracticeToLabel } from '../../model/lessons-learnt/LessonLearntBestPractice';
import { ExportService, ExportWorkBook, ExportWorkSheet, FileExtension } from '../../services/other/export.service';
import { GetProjects } from '../../services/project-list/GetProjects';
import { ProjectStage, stageToLabel } from '../../model/general/ProjectStage';

@Component({
  selector: 'digires-lessons-learnt-overview',
  templateUrl: './lessons-learnt-overview.component.html',
  styleUrls: ['./lessons-learnt-overview.component.scss']
})
export class LessonsLearntOverviewComponent implements OnInit, AfterViewInit {

  destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() project: GetProjects;

  @ViewChild(MatSort) sort: MatSort;

  public readonly projectStages = Object.values(ProjectStage);
  public readonly projectPhases = Object.keys(ProjectPhase);
  public readonly allDiscussedInTeamOptions = Object.keys(DiscussedInTeam);
  public readonly allLessonLearntBestPracticeOptions = Object.keys(LessonLearntBestPractice);
  readonly technologies = Object.keys(Technology);
  public allCountryIds: CountryAlpha2Code[] = [];

  columns = [
    {
      columnDef: 'itemId',
      header: 'Item ID',
      cell: (element: LessonLearnt) => `${element.itemId}`,
      tooltip: "This is the unique identifier reference for this Lesson Learnt Item/Best Practice Item"
    },
    {
      columnDef: 'countryCodes',
      header: 'Countries',
      cell: (element: LessonLearnt) => `${element.countryCodes.map(countryCode => ALL_COUNTRIES.find(country => country.alpha2Code === countryCode).name).join('<br>')}`,
      tooltip: "This Item will be applicable to projects in this Country/Countries"
    },
    {
      columnDef: 'projectId',
      header: 'Project Name',
      cell: (element: LessonLearnt) => `${this.getProjectName(element.projectId)}`,
      tooltip: "The name of the project"
    },
    {
      columnDef: 'createDate',
      header: 'Create Date',
      cell: (element: LessonLearnt) => `${DateUtil.ddmmyyyy(element.createDate)}`,
      tooltip: "The date that the Item was first logged into the Lessons Learnt register"
    },
    {
      columnDef: 'authorUserId',
      header: 'Author',
      cell: (element: LessonLearnt) => `${this.getUserName(element.authorUserId)}`,
      tooltip: "The name of the person who has entered the Item into the Lessons Learnt register"
    },
    {
      columnDef: 'identifierUserId',
      header: 'Identifier',
      cell: (element: LessonLearnt) => `${this.getUserName(element.identifierUserId)}`,
      tooltip: "The name or names of the person or people who have identified the Item"
    },
    {
      columnDef: 'discussedInTeam',
      header: 'Discussed in Team',
      cell: (element: LessonLearnt) => `${this.discussedInTeamToLabel(element.discussedInTeam)}`,
      tooltip: "The relevant members of the Integrated Project Team have been involved in discussions about this matter"
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: LessonLearnt) => `${this.statusAsLabel(element.status)}`,
      tooltip: "This identifies the current Status of the Item as it progresses from: Identification, Consideration and Implementation"
    },
    {
      columnDef: 'technologies',
      header: 'Technologies',
      cell: (element: LessonLearnt) => `${this.getTechnologyName(element.technologies)}`,
      tooltip: "The relevant technologies for this Lesson Learnt"
    },
    {
      columnDef: 'applicableTo',
      header: 'Applicable to',
      cell: (element: LessonLearnt) => `${this.applicableToAsLabel(element.applicableTo)}`,
      tooltip: "This identifies what scope the Item has in terms of how widely it is applicable"
    },
    {
      columnDef: 'lessonsLearnedBestPractice',
      header: 'Lessons Learned / Best Practice',
      cell: (element: LessonLearnt) => `${this.lessonLearntBestPracticeToLabel(element.lessonsLearnedBestPractice)}`,
      tooltip: "Identifies if the Item relates to a Lesson Learnt (i.e. experiences distilled from past activities that should be actively taken into account to inform future actions and behaviours therefore being adopted through Continuous Improvement of our ways of working) or symbolises Best Practice (i.e. optimal performance of the organisation through the application of the most effective ways of working)"

    },
    {
      columnDef: 'projectPhase',
      header: 'Project Phase',
      cell: (element: LessonLearnt) => `${this.projectPhaseAsLabel(element.projectPhase)}`,
      tooltip: "The Project Phase relates to the current phase of the project's life cycle: Development; Construction or Operations."
    },
    {
      columnDef: 'projectStage',
      header: 'Project Stage',
      cell: (element: LessonLearnt) => `${this.projectStageAsLabel(element.projectStage)}`,
      tooltip: "The Project Stage relates to the current stage of the project's life cycle during the Project Phase: 1. Origination; 2. Early Development; 3. Mid-Development; 4. Late Development; 5. Ready for FID (5a. Post FID (Conditionally); 5b. Post FID (Fully Secured)); N/A in Construction; or N/A in Operations."
    },
    {
      columnDef: 'categories',
      header: 'Categories',
      cell: (element: LessonLearnt) => `${this.categoriesAsLabel(element.categoryIds)}`,
      tooltip: "This identifies the relevant categories that the Item is applicable to."
    },
    {
      columnDef: 'detailsOfIssue',
      header: 'Details of Issue',
      cell: (element: LessonLearnt) => `click to read`,
      tooltip: "This identifies the details of the issue that has been identified"
    },
    {
      columnDef: 'mitigationForFutureProjects',
      header: 'Mitigation for future Projects',
      cell: (element: LessonLearnt) => `click to read`,
      tooltip: "This identifies what steps should be taken to ensure negative effects are avoided in the future and that positive effects are harnessed to drive optimal performance"
    },
    {
      columnDef: 'comment',
      header: 'Comment',
      cell: (element: LessonLearnt) => `click to read`,
      tooltip: "This identifies any further comments that the Author wants to make to the Item"
    },
    {
      columnDef: 'severity',
      header: 'Severity',
      cell: (element: LessonLearnt) => `${element.severity}`,
      tooltip: "This identifies how severe the impact of the Item could be on the business: Low; Medium or High"
    },
    {
      columnDef: 'delay',
      header: 'Delay',
      cell: (element: LessonLearnt) => `${element.delay}`,
      tooltip: "This is an indication of the possible number of days the timescales of a project could be impacted by the Item. Delays to project timescales are shown as a positive number; whereas foreshortening of project timescales are shown as a negative number."
    },
    {
      columnDef: 'impactOnBudget',
      header: 'Impact on Budget',
      cell: (element: LessonLearnt) => `${element.impactOnBudget}`,
      tooltip: "This is an indication of the possible impact on the budget for a project shown in millions of the currency selected"
    },
    {
      columnDef: 'currency',
      header: 'Currency',
      cell: (element: LessonLearnt) => `${element.currency || ''}`,
      tooltip: "The currency of the impact on budget"
    },
    {
      columnDef: 'hse',
      header: 'HSE',
      cell: (element: LessonLearnt) => `${element.hse}`,
      tooltip: "This is an indication of the frequency of 'Lost Time Incidents' in relation to Health & Safety matters that could be caused (shown as a positive number) or prevented (shown as a negative number)"
    },
    {
      columnDef: 'quality',
      header: 'Quality',
      cell: (element: LessonLearnt) => `click to read`,
      tooltip: "Enter any information about the relevance of the matter on the Quality of the project"
    },
    {
      columnDef: 'linkToRef',
      header: 'Link to Ref.',
      cell: (element: LessonLearnt) => `click to read`,
      tooltip: "Enter any Link that is relevent"
    },
  ];
  displayedColumns = this.columns.map(c => c.columnDef);

  dataSource = new MatTableDataSource<LessonLearnt>();

  projectIdToProjectName: {[projectId: string]: string} = {};
  userIdToUser: {[userId: string]: GetUsers} = {};

  allCategoriesOptionsIds: String[] = [];
  allCategoriesOptions: DropDownOption[] = [];
  allApplicableToOptions: DropDownOption[] = [];
  allCategoriesDevelopmentOptions: DropDownOption[] = [];
  allCategoriesConstructionOptions: DropDownOption[] = [];
  allStatusOptions: DropDownOption[] = [];

  formGroup: FormGroup;

  panelOpenState = true;

  loading = {
    userLoading: false,
    dropDownConfigLoading: false,
    lessonsLearnt: false,
  };

  constructor(
    private lessonsLearntService: LessonsLearntService,
    private dialog: MatDialog,
    private projectListService: ProjectListService,
    private userService: UserService,
    private dropDownService: DropDownService,
    private fb: FormBuilder,
    private exportService: ExportService,
  ) { }

  async ngOnInit() {
    this.initFormGroup();
    await this.loadData();
  }

  private initFormGroup() {

    this.formGroup = this.fb.group({
      countryCodes: new FormControl(),
      authorUserId: new FormControl(''),
      identifierUserId: new FormControl(''),
      discussedInTeam: new FormControl(),
      lessonsLearnedBestPractice: new FormControl(),
      status: new FormControl(),
      applicableTo: new FormControl(''),
      projectPhase: new FormControl(),
      projectStage: new FormControl(),
      categories: new FormControl(),
      technologies: new FormControl()
    });

    this.formGroup.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(change => {
      this.loadFilterdLessonsLearnt(this.formGroup.value);
    })
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  async loadData() {
    this.allApplicableToOptions = (await this.dropDownService.fetchConfig('lessons-learnt-applicableTo')).values || [];
    this.allCategoriesDevelopmentOptions = (await this.dropDownService.fetchConfig('lessons-learnt-categories-development')).values || [];
    this.allCategoriesConstructionOptions = (await this.dropDownService.fetchConfig('lessons-learnt-categories-construction')).values || [];
    this.allStatusOptions = (await this.dropDownService.fetchConfig('lessons-learnt-status')).values || [];
    this.allCategoriesOptions = this.allCategoriesDevelopmentOptions.concat(this.allCategoriesConstructionOptions);
    this.allCategoriesOptionsIds = this.allCategoriesOptions.map(category => category.id);

    this.lessonsLearntService.observeLessonsLearnt().subscribe(lessonsLearnt => {
      this.loading.lessonsLearnt = false;

      this.dataSource.data = lessonsLearnt;

      lessonsLearnt.forEach(async lessonLearnt => {
        const projectId = lessonLearnt.projectId;
        const project = await this.projectListService.fetchProjectById(projectId);
        this.projectIdToProjectName[projectId] = project ? project.name : '';
      });
    });

    this.allCountryIds = await this.projectListService.getUsedCountryCodes();

    this.userService.observeUserMap().subscribe( userMap => this.userIdToUser = userMap)
    this.userService.fetchAllUsers();
  }

  private loadFilterdLessonsLearnt(formValues: any) {
    this.loading.lessonsLearnt = true;
    const lessonsLearntFilter = new LessonLearntFilter();
    lessonsLearntFilter.applicableTo = formValues.applicableTo ? formValues.applicableTo : '';
    lessonsLearntFilter.authorUserId = formValues.authorUserId?.id ? formValues.authorUserId.id : '';
    lessonsLearntFilter.categoryIds = formValues.categories;
    lessonsLearntFilter.countryCodes = formValues.countryCodes;
    lessonsLearntFilter.discussedInTeam = formValues.discussedInTeam;
    lessonsLearntFilter.identifierUserId = formValues.identifierUserId ? formValues.identifierUserId.id : '';
    lessonsLearntFilter.lessonsLearnedBestPractice = formValues.lessonsLearnedBestPractice;
    lessonsLearntFilter.projectPhase = formValues.projectPhase;
    lessonsLearntFilter.projectStage = formValues.projectStage;
    lessonsLearntFilter.status = formValues.status;
    lessonsLearntFilter.projectId = this.project ? this.project.projectId : '';
    lessonsLearntFilter.technologies = formValues.technologies;

    this.lessonsLearntService.fetchFilteredLessonsLearnt(lessonsLearntFilter);
  }

  public openLessonLearnt(lessonLearnt: LessonLearnt, columnDef: any) {

    this.dialog.open(LessonsLearntDialogComponent, {
      hasBackdrop: true,
      data: {
        project: null,
        lessonLearnt: lessonLearnt,
        column: columnDef
      }
    }).afterClosed().subscribe(async o => {
      this.loadFilterdLessonsLearnt(this.formGroup.value);
    });
  }

  public openAddDialog() {
    this.dialog.open(LessonsLearntDialogComponent, {
        hasBackdrop: true,
        data: {
          project: this.project,
          lessonLearnt: null,
          column: null
        }
      }).afterClosed().subscribe(async o => {
        this.loadFilterdLessonsLearnt(this.formGroup.value);
      });
  }

  public canDownloadExcel() {
    return this.dataSource.data.length > 0;
  }

  public downloadFiltersLessonsLearnt() {
    const workSheet: ExportWorkSheet = {
      name: 'Lessons Learnt - Best Practices',
      header: {},
      rows: []
    }
    this.columns.forEach(column => {
      if(column.columnDef !== 'linkToRef') {
        workSheet.header[column.columnDef] = column.header
      }
    })
    this.dataSource.data.forEach(lessonLearnt => {
      const row = {};
      this.columns.forEach(column => {
        if(['comment', 'detailsOfIssue', 'mitigationForFutureProjects', 'quality', 'linkToRef'].includes(column.columnDef)) {
          row[column.columnDef] = lessonLearnt[column.columnDef]
        } else {
          row[column.columnDef] = (column.cell(lessonLearnt)as string).split('<br>').join(',');
        }
      })
      workSheet.rows.push(row);
    })

    const workBook: ExportWorkBook = {
      fileName: `lessonsLearnt_${DateUtil.ddmmyyyy(new Date())}`,
      extension: FileExtension.XLSX,
      workSheet: [workSheet]
    }

    this.exportService.create(workBook);
  }

  public isLoading() {
    return this.loading.dropDownConfigLoading || this.loading.userLoading || this.loading.lessonsLearnt;
  }

  public getProjectName(projectId: string) {
    const project = this.projectIdToProjectName[projectId]

    return project ? project : 'No Project';
  }

  public getUserName(author: string) {
    return this.userIdToUser[author]?.fullName || '';
  }

  public getTechnologyName(technologies: string[]) {

    const technologyNames = [];

    technologies.forEach(element => {
      technologyNames.push(technologyToLabel[element]);
    });

    return technologyNames.join('<br>');
  }

  public statusAsLabel(statusId: string) {
    if (statusId) {
      return this.allStatusOptions.find(option => option.id === statusId)?.label;
    }
    return '';
  }

  public applicableToAsLabel(applicableToId: string) {
    if (applicableToId) {
      return this.allApplicableToOptions.find(option => option.id === applicableToId)?.label;
    }
    return '';
  }

  public projectPhaseAsLabel(projectPhase: string[]) {
    const projectStageAsLabel = [];
    projectPhase.forEach(phase => {
      projectStageAsLabel.push(phaseToLabel[phase]);
    });
    return projectStageAsLabel.join('<br>');
  }

  public projectStageAsLabel(projectStage: string[]) {
    const projectStageAsLabel = [];
    projectStage.forEach(stage => {
      projectStageAsLabel.push(stageToLabel[stage]);
    });
    return projectStageAsLabel.join('<br>');
  }

  public categoriesAsLabel(categories: string[]) {
    const categoriesAsLabel = [];
    categories.forEach(category => {
      const developmentOption = this.allCategoriesDevelopmentOptions.find(co => co.id === category);
      const constructionOption = this.allCategoriesConstructionOptions.find(co => co.id === category);
      if (developmentOption) {
        categoriesAsLabel.push(developmentOption.label);
      } else if (constructionOption) {
        categoriesAsLabel.push(constructionOption.label);
      }
    });
    return categoriesAsLabel.join('<br>');
  }

  public getStatusOptionsForTooltip() {
    return 'This identifies the current Status of the Item as it progresses from: ' + this.allStatusOptions.map(ddo => ddo.label).toString();
  }

  public dropDownOptionItemToID(value: DropDownOption) {
    return value?.id;
  }

  public dropDownOptionItemToLabel(value: DropDownOption) {
    return value.label;
  }

  public phaseToLabel(phase: ProjectPhase) {
    return phaseToLabel[phase];
  }


  public countryToLabel(countryCode: CountryAlpha2Code) {
    return BY_ALPHA_2[countryCode]?.name;
  }

  public stageToLabel(stage: ProjectStage) {
    return stageToLabel[stage];
  }

  public categoryToLabel(categoryId: String) {
    return this.allCategoriesOptions.find(category => category.id === categoryId).label;
  }

  public discussedInTeamToLabel(discussedInTeam: DiscussedInTeam) {
    return discussedInTeam ? discussedInTeamToLabel[discussedInTeam] : '';
  }

  public lessonLearntBestPracticeToLabel(lessonLearntBestPractice: LessonLearntBestPractice) {
    return lessonLearntBestPracticeToLabel[lessonLearntBestPractice];
  }

  technologyToLabel(technology: Technology) {
    return technologyToLabel[technology];
  }
}
